import { LiveDot } from "@/components/ui/LiveDot";
import { Badge } from "@/components/ui/Badge";
import { formatRelativeTime } from "@/lib/format";
import type { SystemHealth } from "@/types/database";

interface DashboardStatusBarProps {
  planName: string | null;
  isPremiumPlan: boolean;
  workers: SystemHealth[];
}

/** Worst-status-wins aggregation across all reported workers. */
function aggregateWorkerStatus(workers: SystemHealth[]): "healthy" | "idle" | "down" {
  if (workers.length === 0) return "idle";
  if (workers.some((w) => w.status === "down")) return "down";
  if (workers.some((w) => w.status === "degraded" || w.status === "unknown")) return "idle";
  return "healthy";
}

function mostRecentCheck(workers: SystemHealth[]): string | null {
  if (workers.length === 0) return null;
  return workers.reduce((latest, w) => (w.last_check_at > latest ? w.last_check_at : latest), workers[0]!.last_check_at);
}

/**
 * Slim, low-prominence status strip replacing the prior top-billed "System
 * health" card — operational worker status is demoted to a single
 * aggregated dot here, per the UI/UX audit's dashboard hierarchy finding.
 */
export function DashboardStatusBar({ planName, isPremiumPlan, workers }: DashboardStatusBarProps) {
  const aggregateStatus = aggregateWorkerStatus(workers);
  const lastCheck = mostRecentCheck(workers);

  return (
    <div className="dashboard-status-bar">
      <Badge tone={isPremiumPlan ? "premium" : "neutral"}>{planName ?? "Free"}</Badge>
      <span className="dashboard-status-bar-live">
        <LiveDot status={aggregateStatus} label={`System ${aggregateStatus}`} />
        <span className="text-muted font-numeric">
          {lastCheck ? `Updated ${formatRelativeTime(lastCheck)}` : "No health data yet"}
        </span>
      </span>
    </div>
  );
}
